import React from "react";
import styled from "styled-components";
import { IconButton, Modal } from "@mui/material";
import Icon, { IconName } from "./Icon";

export type ModalTone = "gold" | "teal" | "cherry" | "night";

const BUBBLE: Record<ModalTone, [bg: string, fg: string]> = {
  gold: ["var(--gold)", "var(--ink)"],
  teal: ["var(--teal)", "var(--ink)"],
  cherry: ["var(--cherry)", "var(--ink)"],
  night: ["var(--night)", "var(--night-ink)"],
};

const Shell = styled.div<{ $width: number }>`
  position: fixed;
  top: 50%;
  left: 50%;
  translate: -50% -50%;
  width: ${({ $width }) => $width}px;
  max-width: calc(100vw - var(--space-6));
  max-height: calc(100dvh - var(--space-8));
  display: flex;
  flex-direction: column;
  background: var(--card);
  border: var(--border-w) solid var(--ink);
  border-radius: var(--radius-xl);
  box-shadow: 0 6px 0 var(--ink), var(--shadow-float);
  outline: none;
  animation: pop-in var(--dur-slow) var(--ease-bounce) both;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-5) var(--space-5) var(--space-3);

  h2 {
    flex: 1;
    font-size: var(--text-xl);
    font-weight: 600;
    line-height: 1.15;
  }
`;

const Bubble = styled.span<{ $tone: ModalTone }>`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  border: var(--border-w) solid var(--ink);
  background: ${({ $tone }) => BUBBLE[$tone][0]};
  color: ${({ $tone }) => BUBBLE[$tone][1]};
  rotate: -6deg;
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-2) var(--space-5) var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

const Actions = styled.footer`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-5) var(--space-5);
  border-top: var(--border-w) dashed var(--line);
`;

type ModalCardProps = {
  open: boolean;
  onClose?: () => void;
  title: React.ReactNode;
  icon?: IconName;
  tone?: ModalTone;
  width?: number;
  actions?: React.ReactNode;
  children?: React.ReactNode;
};

/**
 * The one modal shell: sticker card, icon bubble, title, close button,
 * scrolling body and a dashed-rule action footer.
 */
const ModalCard: React.FC<ModalCardProps> = ({
  open,
  onClose,
  title,
  icon,
  tone = "gold",
  width = 440,
  actions,
  children,
}) => (
  <Modal open={open} onClose={onClose}>
    <Shell $width={width} role="dialog" aria-modal>
      <Header>
        {icon && (
          <Bubble $tone={tone}>
            <Icon name={icon} size={22} />
          </Bubble>
        )}
        <h2>{title}</h2>
        {onClose && (
          <IconButton onClick={onClose} aria-label="Fechar">
            <Icon name="close" size={22} />
          </IconButton>
        )}
      </Header>
      {children && <Body>{children}</Body>}
      {actions && <Actions>{actions}</Actions>}
    </Shell>
  </Modal>
);

export default ModalCard;
